import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { IconButton, Tooltip } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import axiosInstance from '../../../Config/axiosInstance';
import StatusBadge from '../../../Components/Badges/StatusBadge';
import AddMedical from './AddMedical';
import AddSSM from "./AddSSM";
import './EmployeesDetails.css';
import Medical from "./Medical";
import SSM from "./SSM";

const EmployeesDetails = () => {
  const { id } = useParams(); // id din URL
  const location = useLocation(); // URL curent
  const navigate = useNavigate(); // navigare
  const fileInputRef = useRef(null); // input avatar
  const [employee, setEmployee] = useState(null); // date angajat
  const [loading, setLoading] = useState(true); // stare loading
  const [error, setError] = useState(null); // erori
  const [uploadingAvatar, setUploadingAvatar] = useState(false); // avatar loading
  const [avatarError, setAvatarError] = useState(false); // eroare avatar
  const [localAvatar, setLocalAvatar] = useState(null); // avatar local
  const [activeTab, setActiveTab] = useState('medical'); // tab curent
  const [showAddMedical, setShowAddMedical] = useState(false); // modal medical
  const [showAddSSM, setShowAddSSM] = useState(false); // modal ssm
  const [medicalRecords, setMedicalRecords] = useState([]); // lista medicale
  const [ssmRecords, setSsmRecords] = useState([]); // lista ssm
  const [departments, setDepartments] = useState([]); // departamente
  const [showToast, setShowToast] = useState(false); // toast
  const [toastMessage, setToastMessage] = useState(""); // mesaj toast
  const [contractTypes, setContractTypes] = useState([]); // tipuri contract
  const [canCreateMedical, setCanCreateMedical] = useState(false); // permisiuni medical
  const [canCreateSSM, setCanCreateSSM] = useState(false); // permisiuni ssm
  const decodedCod = decodeURIComponent(id); // decodare id

  const fileToBase64 = (file) => {
    // convert fișier
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const saveAvatarLocally = useCallback((base64Data) => {
    // salvare avatar local
    const avatarKey = `avatar_${decodedCod}`;
    setLocalAvatar(base64Data);

    try {
      sessionStorage.setItem(avatarKey, base64Data);
    } catch (e) {
      console.warn('Nu s-a putut salva în sessionStorage:', e);
    }
  }, [decodedCod]);

  useEffect(() => {
    // detectare tab din URL
    if (location.pathname.includes("/medical")) {
      setActiveTab("medical");
    } else if (location.pathname.includes("/ssm")) {
      setActiveTab("ssm");
    }
  }, [location.pathname]);

  useEffect(() => {
    // fetch date angajat
    const fetchEmployeeDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axiosInstance.get(`/employees/${decodedCod}/medical/`);
        const data = response.data;

        setEmployee(data.employee); // set angajat
        setMedicalRecords(data.medical_checkups || []); // set medicale

        if (data.ssm_records) {
          setSsmRecords(data.ssm_records); // set ssm
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch employee details');
      } finally {
        setLoading(false);
      }
    };

    if (decodedCod) fetchEmployeeDetails();
  }, [decodedCod]);

  useEffect(() => {
    // citire permisiuni
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const pages = storedPermissions?.pages || [];

    const checkPermission = (path, key) => {
      // verificare pagina
      try {
        return pages.some((page) => {
          const pagePath = new URL(page.url).pathname;
          return pagePath === path && page[key] === true;
        });
      } catch {
        return false;
      }
    };

    setCanCreateMedical(checkPermission("/employees/:id/medical", "can_create"));
    setCanCreateSSM(checkPermission("/employees/:id/ssm", "can_create"));
  }, []);

  useEffect(() => {
    // fetch SSM separat
    const fetchSSMRecords = async () => {
      if (!decodedCod) return;

      try {
        const response = await axiosInstance.get(`/employees/${decodedCod}/ssm/`);
        const data = response.data;
        setSsmRecords(data.ssm_records || []);
      } catch (err) {
        console.error('Error fetching SSM records:', err);
      }
    };

    if (ssmRecords.length === 0) {
      fetchSSMRecords();
    }
  }, [decodedCod, ssmRecords.length]);

  useEffect(() => {
    // fetch departamente
    const fetchDepartments = async () => {
      try {
        const res = await axiosInstance.get('/departments/');
        setDepartments(res.data);
      } catch (err) {
        console.error('Eroare la preluarea departamentelor:', err);
      }
    };
    fetchDepartments();
  }, []);

  useEffect(() => {
    // fetch tipuri contract
    const fetchContractTypes = async () => {
      try {
        const res = await axiosInstance.get('/type-contract/');
        setContractTypes(res.data || []);
      } catch (err) {
        console.error('Eroare la preluarea tipurilor de contract:', err);
      }
    };
    fetchContractTypes();
  }, []);

  const handleBackClick = () => {
    // back la employees
    navigate('/employees');
  };

  const handleCloseAddMedical = () => {
    // închidere modal medical
    setShowAddMedical(false);
  };

  const handleMedicalSuccess = () => {
    // success adăugare medical
    setShowAddMedical(false);

    setToastMessage("✅ Medical record added successfully!");
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);

    const fetchMedicalData = async () => {
      try {
        const response = await axiosInstance.get(`/employees/${decodedCod}/medical/`);
        setMedicalRecords(response.data.medical_checkups || []);
      } catch (err) {
        console.error("Error refreshing medical data:", err);
      }
    };
    fetchMedicalData();
  };

  const handleEditMedicalSuccess = () => {
    // succes edit medical
    setToastMessage("✅ Medical record updated successfully!");
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);

    const fetchMedicalData = async () => {
      try {
        const response = await axiosInstance.get(`/employees/${decodedCod}/medical/`);
        setMedicalRecords(response.data.medical_checkups || []);
      } catch (err) {
        console.error("Error refreshing medical data after edit:", err);
      }
    };
    fetchMedicalData();
  };

  const handleEditSSMSuccess = () => {
    // succes edit ssm
    setToastMessage("✅ SSM record updated successfully!");
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);

    const fetchSSMData = async () => {
      try {
        const response = await axiosInstance.get(`/employees/${decodedCod}/ssm/`);
        setSsmRecords(response.data.ssm_records || []);
      } catch (err) {
        console.error("Error refreshing SSM data after edit:", err);
      }
    };
    fetchSSMData();
  };

  const handleCloseAddSSM = () => setShowAddSSM(false); // închidere modal ssm

  const handleSSMSuccess = () => {
    // succes adăugare ssm
    setShowAddSSM(false);

    setToastMessage("✅ SSM record added successfully!");
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);

    const fetchSSMData = async () => {
      try {
        const response = await axiosInstance.get(`/employees/${decodedCod}/ssm/`);
        setSsmRecords(response.data.ssm_records || []);
      } catch (err) {
        console.error("Error refreshing SSM data:", err);
      }
    };
    fetchSSMData();
  };

  const getStatusVariant = (status) => {
    // variantă badge
    if (!status) return 'default';
    const normalized = status.trim().toLowerCase();
    switch (normalized) {
      case 'active':
        return 'success';
      case 'activ':
        return 'success';
      case 'inactive':
      case 'inactiv':
      case 'expirat':
      case 'critical':
        return 'critical';
      case 'pending':
        return 'pending';
      case 'completed':
        return 'success';
      default:
        return 'default';
      case 'suspended':
        return 'pending';
    }
  };

  const getDepartmentName = (departmentId) => {
    // nume departament
    const dep = departments.find((d) => d.id === departmentId);
    return dep ? (dep.denumire || dep.name || dep.title) : departmentId;
  };

  const getTypeName = (typeId) => {
    // nume contract
    if (!typeId) return "-";

    const contract = contractTypes.find((t) => t.id === typeId);
    if (contract) return contract.name || contract.type || contract.denumire || "-";
  };

  const handleAvatarClick = () => {
    // click avatar
    if (uploadingAvatar) return;
    fileInputRef.current?.click();
  };

  const getAvatarSrc = () => {
    // sursa avatar
    if (localAvatar) return localAvatar;
    if (!employee || !employee.avatar) return null;

    const base = axiosInstance.defaults.baseURL?.replace(/\/$/, "") || "";
    let avatarUrl = employee.avatar;

    if (avatarUrl.startsWith("http")) return avatarUrl;
    if (avatarUrl.startsWith("/")) return `${base}${avatarUrl}`;

    return `${base}/media/avatars/${avatarUrl}`;
  };

  const handleFileChange = async (event) => {
    // schimbare avatar
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Imagine invalidă');
      event.target.value = '';
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Imagine prea mare');
      event.target.value = '';
      return;
    }

    setUploadingAvatar(true);
    setAvatarError(false);

    try {
      const base64Data = await fileToBase64(file);
      saveAvatarLocally(base64Data);

      const formData = new FormData();
      formData.append('avatar', file);

      await axiosInstance.put(`/employees/${encodeURIComponent(decodedCod)}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });

      setEmployee(prev => ({ ...prev, avatar: file.name }));

      setToastMessage("✅ Avatar updated successfully!");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    } catch (error) {
      console.error('❌ Eroare la încărcarea avatarului:', error.response?.data || error);
      setToastMessage("❌ Failed to update avatar.");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    } finally {
      setUploadingAvatar(false);
      event.target.value = '';
    }
  };

  const handleTabChange = (tab) => {
    // schimbare tab
    setActiveTab(tab);
    navigate(`/employees/${decodedCod}/${tab}`);
  };

  if (loading) {
    // loading UI
    return (
      <div className="loading-container">
        <div>Loading...</div>
      </div>
    );
  }

  if (error || !employee) {
    // eroare UI
    return (
      <div className="error-container">
        <div className="error-message">
          {error || 'Employee not found'}
        </div>
        <button onClick={handleBackClick} className="back-button">
          Back to Employees
        </button>
      </div>
    );
  }

  const avatarSrc = getAvatarSrc(); // avatar final

  return (
    <>
      {showToast && <div className="employees-toast">{toastMessage}</div>}
      <div className="page-employeesdetails">
        <div className="employees-details-container">

          <div className="main-card">
            <div className="card-content">
              <button
                onClick={handleBackClick}
                className="back-button-header"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <div className="employee-header">
                <div className="avatar-section">
                  <div className="avatar-container">
                    {uploadingAvatar && (
                      <div className="avatar-overlay">
                        <div className="spinner" />
                        <div className="uploading-text">
                          Uploading...
                        </div>
                      </div>
                    )}

                    {avatarSrc && !avatarError ? (
                      <img
                        src={avatarSrc}
                        alt={`${employee.full_name || 'Employee'} avatar`}
                        className="avatar-image"
                        onLoad={() => setAvatarError(false)}
                        onError={() => setAvatarError(true)}
                      />
                    ) : (
                      <div className="avatar-placeholder">
                        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </div>
                    )}
                  </div>

                  <Tooltip title="Schimbă avatar">
                    <IconButton
                      onClick={handleAvatarClick}
                      disabled={uploadingAvatar}
                      className="avatar-edit-button"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="file-input"
                    disabled={uploadingAvatar}
                  />
                </div>

                {/* info angajat */}
                <div className="employee-info">
                  <div className="name-section">
                    <h1 className="employee-name">
                      {employee.first_name && employee.last_name
                        ? `${employee.first_name} ${employee.last_name}`
                        : employee.first_name || employee.last_name || 'Name not available'}
                    </h1>

                    <StatusBadge
                      variant={getStatusVariant(employee.status)}
                      text={
                        employee.status
                          ? employee.status.charAt(0).toUpperCase() + employee.status.slice(1).toLowerCase()
                          : 'No status'
                      }
                    />

                  </div>
                  {/* grid info */}
                  <div className="info-grid">
                    <div className="info-item">
                      <svg xmlns="http://www.w3.org/2000/svg" width="17" viewBox="0 0 24 24">
                        <path d="m14.662 23.038.012.007c2.46 1.566 5.71 1.21 7.792-.873l.774-.774a2.596 2.596 0 0 0 0-3.669l-3.26-3.26a2.596 2.596 0 0 0-3.67 0 1.093 1.093 0 0 1-1.546.002l-.001-.001-5.219-5.22a1.096 1.096 0 0 1 0-1.548 2.593 2.593 0 0 0 .002-3.666q0-.002-.002-.003L6.284.77a2.596 2.596 0 0 0-3.669 0l-.774.774A6.285 6.285 0 0 0 .982 9.36L1 9.386a50.7 50.7 0 0 0 13.62 13.625zm.798-1.27A49.2 49.2 0 0 1 2.244 8.55l-.005-.008a4.78 4.78 0 0 1 .662-5.938l.774-.774a1.096 1.096 0 0 1 1.549 0l3.26 3.264v.002a1.09 1.09 0 0 1 0 1.545 2.596 2.596 0 0 0 0 3.67l5.218 5.22.002.001a2.593 2.593 0 0 0 3.667-.002 1.096 1.096 0 0 1 1.548 0l3.26 3.26c.428.427.428 1.12 0 1.548l-.773.774a4.78 4.78 0 0 1-5.951.652l-.037-.022z" fill="#303030" />
                      </svg>
                      <span className="info-text phone">
                        {employee.phone || 'No phone'}
                      </span>
                    </div>

                    <div className="info-item">
                      <svg width="17" height="17" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_5260_18941)">
                          <path d="M1.9446 7.34442C1.9446 4.18959 4.50212 1.63209 7.65697 1.63209C10.8118 1.63209 13.3693 4.18959 13.3693 7.34442C13.3693 7.79511 13.7347 8.16047 14.1854 8.16047C14.6361 8.16047 15.0014 7.79511 15.0014 7.34442C15.0014 3.28821 11.7132 0 7.65697 0C3.60073 0 0.3125 3.28821 0.3125 7.34442C0.3125 11.4006 3.60073 14.6888 7.65697 14.6888C8.10766 14.6888 8.47302 14.3235 8.47302 13.8728C8.47302 13.4221 8.10766 13.0568 7.65697 13.0568C4.50212 13.0568 1.9446 10.4993 1.9446 7.34442Z" fill="#303030" />
                          <path d="M9.32968 7.13261C9.3924 7.57892 9.80505 7.88987 10.2514 7.82714C10.6977 7.76442 11.0086 7.35177 10.9459 6.90547C10.8621 6.30934 10.6151 5.74799 10.2322 5.28348C9.84929 4.81897 9.34538 4.46939 8.77618 4.2734C8.20698 4.07742 7.59468 4.04265 7.00696 4.17294C6.41923 4.30324 5.87899 4.59352 5.44595 5.01169C5.01291 5.42987 4.70395 5.95965 4.55323 6.54247C4.4025 7.12528 4.41588 7.73843 4.59188 8.31411C4.76789 8.8898 5.09966 9.4056 5.55053 9.80449C6.0014 10.2034 6.55379 10.4698 7.14664 10.5744C7.59048 10.6526 8.01373 10.3563 8.092 9.91241C8.17026 9.46856 7.87389 9.04532 7.43005 8.96705C7.13362 8.91479 6.85743 8.78157 6.63199 8.58212C6.40656 8.38268 6.24067 8.12477 6.15267 7.83693C6.06467 7.54909 6.05798 7.24252 6.13334 6.95111C6.20871 6.6597 6.36319 6.39481 6.5797 6.18572C6.79622 5.97664 7.06635 5.8315 7.36021 5.76635C7.65407 5.7012 7.96022 5.71859 8.24482 5.81658C8.52942 5.91457 8.78137 6.08936 8.97283 6.32162C9.16429 6.55387 9.28779 6.83455 9.32968 7.13261Z" fill="#303030" />
                          <path d="M9.40978 8.40324C9.21222 8.33351 8.99217 8.38342 8.84402 8.53157C8.69588 8.67971 8.64597 8.89976 8.7157 9.09732L11.0238 15.637C11.0906 15.8261 11.2559 15.9631 11.454 15.9936C11.6522 16.0242 11.8511 15.9432 11.9716 15.783L13.2465 14.0881L14.3754 15.2169C14.5878 15.4294 14.9323 15.4294 15.1447 15.2169L15.5294 14.8323C15.7419 14.6198 15.7419 14.2753 15.5294 14.0629L14.4006 12.934L16.0955 11.6592C16.2557 11.5386 16.3367 11.3397 16.3061 11.1416C16.2756 10.9434 16.1386 10.7781 15.9495 10.7114L9.40978 8.40324Z" fill="#303030" />
                        </g>
                        <defs>
                          <clipPath id="clip0_5260_18941">
                            <rect width="16" height="16" fill="white" transform="translate(0.3125)" />
                          </clipPath>
                        </defs>
                      </svg>
                      <span className="info-text">
                        {getTypeName(employee.role) || employee.role || 'No role'}
                      </span>
                    </div>

                    <div className="info-item">
                      <svg width="17" height="17" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.9525 11.3872L7.18579 15.1472V7.62719L13.9525 3.86719V11.3872Z" fill="#303030" />
                        <path d="M5.43262 13.393H4.43262V5.91302L11.886 1.67969L12.3793 2.54635L5.43262 6.49969V13.393Z" fill="#303030" />
                        <path d="M3.17261 11.1339H2.17261V4.3205L9.63258 0.173828L10.1192 1.04716L3.17261 4.90716V11.1339Z" fill="#303030" />
                      </svg>
                      <span className="info-text">
                        {getDepartmentName(employee.department) || 'No department'}
                      </span>
                    </div>

                    <div className="info-item">
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 2a2 2 0 0 0-2 2v16l4-2 4 2 4-2 4 2V4a2 2 0 0 0-2-2H6zm0 2h12v13.17l-2-.99-4 2-4-2-2 1V4zm2 3h8v2H8V7zm0 4h8v2H8v-2z"
                          fill="#303030"
                        />
                      </svg>

                      <span className="info-text">
                        {getTypeName(employee.type) || 'No contract type'}
                      </span>
                    </div>

                    {/* EMAIL - NOU */}
                    <div className="info-item">
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z" fill="#303030"/>
                      </svg>
                      <span className="info-text">
                        {employee.email || 'No email'}
                      </span>
                    </div>

                    <div className="info-item">
                      <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_5260_19087)">
                          <mask id="mask0_5260_19087" maskUnits="userSpaceOnUse" x="0" y="0" width="17" height="16">
                            <path d="M16.3125 0H0.3125V16H16.3125V0Z" fill="white" />
                          </mask>
                          <g mask="url(#mask0_5260_19087)">
                            <path d="M9.79207 1.33398H5.71877C4.89877 1.33398 4.23877 1.99398 4.23877 2.81398V4.51398L5.62544 3.82065C6.16544 3.54065 6.8121 3.56732 7.3454 3.88732C7.8854 4.22732 8.19874 4.79398 8.19874 5.40732L8.23874 14.6673H11.2721V2.81398C11.2721 1.99398 10.6121 1.33398 9.79207 1.33398Z" fill="#303030" />
                            <path d="M13.4995 8H11.9395V14.6667H13.4995C14.3195 14.6667 14.9795 14.0067 14.9795 13.1867V9.48C14.9795 8.66 14.3195 8 13.4995 8Z" fill="#303030" />
                            <path d="M7.00575 4.46132C6.67242 4.26132 6.26575 4.24132 5.92575 4.41466L4.23909 5.26132L2.46575 6.14799C1.96575 6.39466 1.64575 6.90797 1.64575 7.47464V13.188C1.64575 14.008 2.30575 14.668 3.12575 14.668H7.57242L7.53242 5.40799C7.53242 5.02132 7.33242 4.66799 7.00575 4.46132ZM5.35242 12.208H3.87242C3.59909 12.208 3.37242 11.9813 3.37242 11.708C3.37242 11.4346 3.59909 11.208 3.87242 11.208H5.35242C5.62575 11.208 5.85242 11.4346 5.85242 11.708C5.85242 11.9813 5.62575 12.208 5.35242 12.208ZM5.35242 9.98797H3.87242C3.59909 9.98797 3.37242 9.7613 3.37242 9.48797C3.37242 9.21464 3.59909 8.98797 3.87242 8.98797H5.35242C5.62575 8.98797 5.85242 9.21464 5.85242 9.48797C5.85242 9.7613 5.62575 9.98797 5.35242 9.98797Z" fill="#303030" />
                          </g>
                        </g>
                        <defs>
                          <clipPath id="clip0_5260_19087">
                            <rect width="16" height="16" fill="white" transform="translate(0.3125)" />
                          </clipPath>
                        </defs>
                      </svg>
                      <span className="info-text">
                        {employee.adress || 'No Address'}
                      </span>
                    </div>

                    <div className="info-item">
                      <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M5.55769 0.944444C5.55769 0.422842 5.11858 0 4.57692 0C4.03526 0 3.59615 0.422842 3.59615 0.944444V1.45653C3.27463 1.529 2.97013 1.63039 2.67755 1.77395C1.75483 2.22669 1.00464 2.94909 0.534488 3.83764C0.238504 4.39702 0.115111 5.00164 0.0567067 5.69C-1.68701e-05 6.35855 -9.23025e-06 7.18413 2.43957e-07 8.2077V10.0515C-9.23025e-06 11.0751 -1.68701e-05 11.9007 0.0567067 12.5692C0.115111 13.2576 0.238504 13.8622 0.534488 14.4216C1.00464 15.3102 1.75483 16.0326 2.67755 16.4853C3.25845 16.7703 3.88632 16.8891 4.60116 16.9454C5.29542 17 6.15275 17 7.21568 17L11.6402 17C12.0616 17 12.3485 17.0001 12.6014 16.9749C14.91 16.7446 16.7347 14.9874 16.9739 12.7644C17.0001 12.5208 17.0001 12.2445 17 11.8388L17 8.20769C17 7.18413 17 6.35855 16.9433 5.69C16.8849 5.00164 16.7615 4.39702 16.4655 3.83764C15.9954 2.94909 15.2452 2.22669 14.3224 1.77395C14.0299 1.63039 13.7254 1.529 13.4038 1.45653V0.944444C13.4038 0.422842 12.9647 0 12.4231 0C11.8814 0 11.4423 0.422842 11.4423 0.944444V1.27007C10.9539 1.25925 10.404 1.25925 9.78429 1.25926H7.21571C6.59595 1.25925 6.04609 1.25925 5.55769 1.27007V0.944444ZM3.56807 3.45696C3.82636 3.33023 4.16736 3.24318 4.76089 3.19648C5.36587 3.14888 6.14294 3.14815 7.25769 3.14815H9.7423C10.8571 3.14815 11.6341 3.14888 12.2391 3.19648C12.8326 3.24318 13.1736 3.33023 13.4319 3.45696C13.9856 3.7286 14.4357 4.16205 14.7178 4.69517C14.8494 4.9439 14.9398 5.27227 14.9883 5.84382C15.0076 6.07118 15.0194 6.32378 15.0267 6.61111H1.97325C1.98056 6.32378 1.99244 6.07118 2.01173 5.84382C2.06022 5.27227 2.15062 4.9439 2.28223 4.69517C2.56432 4.16205 3.01443 4.7286 3.56807 3.45696ZM1.96154 8.49999V10.0111C1.96154 11.0846 1.9623 11.8329 2.01173 12.4154C2.06022 12.987 2.15062 13.3154 2.28223 13.5641C2.56432 14.0972 3.01443 14.5306 3.56807 14.8023C3.82636 14.929 4.16736 15.0161 4.76089 15.0628C5.36587 15.1104 6.14294 15.1111 7.25769 15.1111H11.5805C12.0839 15.1111 12.2608 15.1098 12.3993 15.096C13.7844 14.9578 14.8793 13.9035 15.0228 12.5697C15.0371 12.4364 15.0385 12.266 15.0385 11.7813V8.49999H1.96154Z" fill="#303030" />
                      </svg>
                      <span className="info-text">
                        {employee.contract_date ?
                          new Date(employee.contract_date).toLocaleDateString("ro-RO") :
                          'No contract date'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {/* tabs */}
              <div className="tabs-container">
                <div className="tabs-header">
                  <div className="tabs-left">
                    <button
                      onClick={() => handleTabChange('medical')}
                      className={`tab-button ${activeTab === 'medical' ? 'active' : ''}`}
                    >
                      Medical
                    </button>

                    <button
                      onClick={() => handleTabChange('ssm')}
                      className={`tab-button ${activeTab === 'ssm' ? 'active' : ''}`}
                    >
                      SSM
                    </button>

                  </div>

                  <div className="tabs-right">
                    {/* ADD medical */}
                    {activeTab === 'medical' &&
                      canCreateMedical &&
                      medicalRecords.length > 0 && (
                        <button className="tab-add-btn" onClick={() => setShowAddMedical(true)}>
                          <span className="plus">+</span> ADD NEW MEDICAL
                        </button>
                      )}
                    {/* ADD SSM */}
                    {activeTab === 'ssm' &&
                      canCreateSSM &&
                      ssmRecords.length > 0 && (
                        <button className="tab-add-btn" onClick={() => setShowAddSSM(true)}>
                          <span className="plus">+</span> ADD NEW SSM
                        </button>
                      )}
                  </div>
                </div>
              </div>
              {/* conținut tab */}
              <div className="tab-content">
                {activeTab === 'medical' ? (
                  <Medical
                    employee={employee}
                    medicalRecords={medicalRecords}
                    showAddMedical={showAddMedical}
                    setShowAddMedical={setShowAddMedical}
                    onEditSuccess={handleEditMedicalSuccess}
                  />

                ) : (
                  <SSM
                    employee={employee}
                    ssmRecords={ssmRecords}
                    showAddSSM={showAddSSM}
                    setShowAddSSM={setShowAddSSM}
                    onEditSuccess={handleEditSSMSuccess}
                    onAddSuccess={handleSSMSuccess}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        {/* modale */}
        <AddMedical
          isOpen={showAddMedical}
          onClose={handleCloseAddMedical}
          employeeData={employee}
          onSuccess={handleMedicalSuccess}
        />

        <AddSSM
          isOpen={showAddSSM}
          onClose={handleCloseAddSSM}
          employerData={employee}
          onSuccess={handleSSMSuccess}
        />
      </div>
    </>
  );
};

export default EmployeesDetails;